import React, { useState, useMemo } from 'react'
import { Breadcrumb, Container } from 'react-bootstrap'
import { useGetInternalAdmins } from 'hooks/use-get_internal-admins'
import useOrganizationsMissingInfo from 'hooks/use-organizations-missing-info'
import { useOrganizationAssignments, useOrganizationFilters } from 'hooks'
import { getEntityType } from 'utils/organization-utils'

import {
  OrganizationFilters,
  StatusTabs,
  OrganizationTable,
  Pagination,
  EmptyState,
} from 'components/organizations-missing-info'

const OrganizationsMissingInfoPage = () => {
  const [currentPage, setCurrentPage] = useState(1)
  const [perPage] = useState(20)

  const {
    data,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useOrganizationsMissingInfo()
  const { data: internalAdmins } = useGetInternalAdmins()

  const allLoadedOrganizations = useMemo(() => {
    if (!data?.pages) return []
    const organizations = data.pages.flatMap((page) => page.organizations || [])

    return organizations
  }, [data?.pages])

  const {
    searchTerm,
    setSearchTerm,
    accountManagerFilter,
    setAccountManagerFilter,
    salesRepFilter,
    setSalesRepFilter,
    sortOrder,
    setSortOrder,
    showUpdatedOnly,
    setShowUpdatedOnly,
    filteredAndSortedOrganizations,
  } = useOrganizationFilters(allLoadedOrganizations)

  const {
    selectedAssignments,
    handleAssignmentChange,
    isValidId,
    getSelectedId,
    clearAssignment,
  } = useOrganizationAssignments(internalAdmins || [])

  const totalFilteredPages = Math.ceil(
    filteredAndSortedOrganizations.length / perPage
  )
  const startIndex = (currentPage - 1) * perPage
  const endIndex = startIndex + perPage
  const currentPageOrganizations = filteredAndSortedOrganizations.slice(
    startIndex,
    endIndex
  )

  React.useEffect(() => {
    setCurrentPage(1)
  }, [
    searchTerm,
    accountManagerFilter,
    salesRepFilter,
    sortOrder,
    showUpdatedOnly,
  ])

  React.useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const handleSaveSuccess = () => {
    clearAssignment('')
    refetch()
  }

  return (
    <Container>
      <Breadcrumb>
        <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
        <Breadcrumb.Item href="/organizations">Organizations</Breadcrumb.Item>
        <Breadcrumb.Item active>Organizations Missing Info</Breadcrumb.Item>
      </Breadcrumb>

      <div className="d-flex align-items-center gap-3 mb-3 flex-wrap">
        <StatusTabs
          showUpdatedOnly={showUpdatedOnly}
          setShowUpdatedOnly={setShowUpdatedOnly}
          filteredCount={filteredAndSortedOrganizations.length}
          allOrganizations={allLoadedOrganizations}
        />
        <OrganizationFilters
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          accountManagerFilter={accountManagerFilter}
          setAccountManagerFilter={setAccountManagerFilter}
          salesRepFilter={salesRepFilter}
          setSalesRepFilter={setSalesRepFilter}
          sortOrder={sortOrder}
          setSortOrder={setSortOrder}
          internalAdmins={internalAdmins}
        />
      </div>

      {isFetching && allLoadedOrganizations.length === 0 ? (
        <div className="m-2">Loading...</div>
      ) : (
        <>
          <OrganizationTable
            organizations={currentPageOrganizations}
            internalAdmins={internalAdmins || []}
            selectedAssignments={selectedAssignments}
            handleAssignmentChange={handleAssignmentChange}
            getSelectedId={getSelectedId}
            isValidId={isValidId}
            getEntityType={getEntityType}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
            onSaveSuccess={handleSaveSuccess}
            isUpdatedPage={showUpdatedOnly}
          />

          <Pagination
            currentPage={currentPage}
            totalPages={totalFilteredPages}
            onPageChange={setCurrentPage}
          />

          <EmptyState
            showUpdatedOnly={showUpdatedOnly}
            allLoadedOrganizations={allLoadedOrganizations}
            isFetching={isFetching}
          />
        </>
      )}
    </Container>
  )
}

export default OrganizationsMissingInfoPage
